'use client';

import Image from 'next/image';
import { Container } from '@mantine/core';
import { Carousel } from '@mantine/carousel';
import styles from './ClassicQuote.module.scss';

export type QuoteItem = {
  text: string;
  author?: string;

  // optional
  rating?: 1 | 2 | 3 | 4 | 5;
  date?: string;
  isLocalGuide?: boolean;
  avatar?: { url: string; alt?: string };

  image?: {
    url: string;
    alt?: string;
    position?: 'left' | 'right';
  };
};

export type ClassicQuoteSlice = {
  type?: 'premium-quote-carousel';
  eyebrow?: string;
  url?: string;
  align?: 'left' | 'center';

  // dynamic global values from slice
  globalRating?: number;
  reviewCount?: number;

  // optional backward compatibility
  overallRating?: number;
  overallCount?: number;

  items: QuoteItem[];
};

function clampRating(n: number) {
  return Math.max(1, Math.min(5, n));
}

function computeAvg(items: QuoteItem[]) {
  const ratings = items
    .map((i) => i.rating)
    .filter((r): r is 1 | 2 | 3 | 4 | 5 => typeof r === 'number' && r >= 1 && r <= 5);

  if (!ratings.length) return null;

  const sum = ratings.reduce((a, b) => a + b, 0);
  return Math.round((sum / ratings.length) * 10) / 10;
}

function GoogleGIcon() {
  return (
    <svg className={styles.googleG} viewBox="0 0 256 262" aria-hidden="true" focusable="false">
      <path
        fill="#4285F4"
        d="M255.7 133.5c0-11-1-22-3-32.7H130.7v61.9h69.7c-3 16.2-12.2 30-25.8 39.2v32.3h41.7c24.4-22.5 39.4-55.7 39.4-100.7z"
      />
      <path
        fill="#34A853"
        d="M130.7 261.1c35.1 0 64.6-11.6 86.1-31.4l-41.7-32.3c-11.6 7.8-26.4 12.5-44.4 12.5-34.1 0-63-23-73.3-53.9H14.3v33.8c21.4 42.5 65.4 71.3 116.4 71.3z"
      />
      <path
        fill="#FBBC05"
        d="M57.4 156c-2.7-8.1-4.2-16.7-4.2-25.6s1.5-17.5 4.2-25.6V71H14.3C5.1 89.2 0 109.9 0 130.4s5.1 41.2 14.3 59.4L57.4 156z"
      />
      <path
        fill="#EA4335"
        d="M130.7 50.1c19.1 0 36.3 6.6 49.8 19.5l37.3-37.3C195.2 11.3 165.7 0 130.7 0 79.7 0 35.7 28.8 14.3 71l43.1 33.8c10.3-30.9 39.2-53.9 73.3-53.9z"
      />
    </svg>
  );
}

function Stars({ value }: { value: number }) {
  const v = clampRating(Math.round(value));

  return (
    <div className={styles.stars} role="img" aria-label={`${v} sur 5`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <span key={i} className={i < v ? styles.starOn : styles.starOff} aria-hidden>
          ★
        </span>
      ))}
    </div>
  );
}

export function ClassicQuote({ data }: { data: ClassicQuoteSlice }) {
  if (!data.items?.length) return null;

  const computedAvg = computeAvg(data.items);

  const headerAvg =
    typeof data.globalRating === 'number'
      ? data.globalRating
      : typeof data.overallRating === 'number'
        ? data.overallRating
        : computedAvg;

  const headerCount =
    typeof data.reviewCount === 'number'
      ? data.reviewCount
      : typeof data.overallCount === 'number'
        ? data.overallCount
        : data.items.length;

  return (
    <section className={styles.section}>
      <Container size="lg">
        <div className={[styles.root, data.align === 'left' ? styles.left : styles.center].join(' ')}>
          {data.eyebrow && <div className={styles.eyebrow}>{data.eyebrow}</div>}

          <div className={styles.card}>
            <div className={styles.header}>
              <div className={styles.headerLeft}>
                <GoogleGIcon />
                <div className={styles.wordmarkWrap}>
                  <div className={styles.wordmark}>Google</div>
                  <div className={styles.sub}>Avis</div>
                </div>
              </div>

              <div className={styles.headerRight}>
                {typeof headerAvg === 'number' ? (
                  <div className={styles.avgBlock}>
                    <span className={styles.avgNumber}>{headerAvg.toFixed(1)}</span>
                    <Stars value={headerAvg} />
                  </div>
                ) : (
                  <div className={styles.avgBlock}>
                    <span className={styles.avgNumber}>—</span>
                  </div>
                )}

                <div className={styles.headerMetaRow}>
                  <div className={styles.count}>
                    {headerCount} {headerCount > 1 ? 'avis' : 'avis'}
                  </div>

                  {data.url && (
                    <a
                      href={data.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.googleLink}
                      aria-label="Voir tous les avis Google"
                    >
                      <span className={styles.googleLinkDot} aria-hidden="true" />
                      Voir sur Google
                    </a>
                  )}
                </div>
              </div>
            </div>

            <Carousel
              emblaOptions={{
                loop: true,
                align: 'center',
              }}
              slideSize="100%"
              slideGap={0}
              withIndicators
              withControls
              draggable
              controlSize={44}
              nextControlProps={{ 'aria-label': "Afficher l'avis suivant" }}
              previousControlProps={{ 'aria-label': "Afficher l'avis précédent" }}
              classNames={{
                root: styles.carousel,
                viewport: styles.viewport,
                container: styles.container,
                slide: styles.slide,
                controls: styles.controls,
                control: styles.control,
                indicators: styles.indicators,
                indicator: styles.indicator,
              }}
            >
              {data.items.map((item, index) => {
                const hasImage = !!item.image?.url;
                const imagePosition = item.image?.position ?? 'left';

                return (
                  <Carousel.Slide key={index}>
                    <div
                      className={[
                        styles.slideInner,
                        hasImage ? styles.withImage : styles.noImage,
                        hasImage && imagePosition === 'right' ? styles.imageRight : styles.imageLeft,
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {hasImage && (
                        <div className={styles.mediaCol}>
                          <div className={styles.squareMedia}>
                            <Image
                              src={item.image!.url}
                              alt={item.image?.alt || 'Réalisation client'}
                              fill
                              sizes="(max-width: 768px) 88vw, 420px"
                              className={styles.squareImg}
                              priority={index < 2}
                              loading="eager"
                            />
                          </div>
                        </div>
                      )}

                      <div className={styles.textCol}>
                        <p className={styles.quote}>“{item.text}”</p>

                        <div className={styles.metaRow}>
                          {item.author && <div className={styles.author}>{item.author}</div>}
                          {(item.isLocalGuide || item.date) && (
                            <div className={styles.metaChips}>
                              {item.isLocalGuide && <span className={styles.chip}>Local Guide</span>}
                              {item.date && <span className={styles.date}>{item.date}</span>}
                            </div>
                          )}
                        </div>

                        {typeof item.rating === 'number' && (
                          <div className={styles.ratingRow}>
                            <Stars value={item.rating} />
                          </div>
                        )}
                      </div>
                    </div>
                  </Carousel.Slide>
                );
              })}
            </Carousel>
          </div>
        </div>
      </Container>
    </section>
  );
}